import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DocumentChartBarIcon, ArrowLeftIcon, ArrowDownTrayIcon,
  ArrowPathIcon, TrashIcon, ExclamationCircleIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { ReportService } from '../../../shared/services/reportService';
import ReportStatusBadge from '../components/ReportStatusBadge';
import ReportTypeBadge from '../components/ReportTypeBadge';
import DownloadFormatModal from '../components/DownloadFormatModal';

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatDateOnly = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MetaRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <span className="text-sm text-gray-500 dark:text-gray-400 min-w-[140px]">{label}</span>
    <span className="text-sm text-gray-900 dark:text-white text-right">{children ?? '—'}</span>
  </div>
);

const Toast = ({ message, type, onDismiss }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm ${type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
    {message}
    <button onClick={onDismiss}><XMarkIcon className="w-4 h-4" /></button>
  </div>
);

const ROWS_PER_PAGE = 25;

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDownload, setShowDownload] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState(null);

  const [rowsPage, setRowsPage] = useState(1);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ReportService.detail(id);
      setReport(res.data?.data ?? res.data);
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to load report details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await ReportService.regenerate(id);
      showToast('Report regeneration queued.', 'success');
      fetchDetail();
    } catch {
      showToast('Failed to regenerate report.', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    try {
      await ReportService.delete(id);
      navigate('/admin/reports');
    } catch {
      showToast('Failed to delete report.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="animate-pulse space-y-4 max-w-4xl mx-auto">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex flex-col items-center justify-center gap-4 text-red-500">
        <ExclamationCircleIcon className="w-12 h-12" />
        <p className="text-sm font-medium">{error}</p>
        <button onClick={fetchDetail} className="text-sm underline">Retry</button>
      </div>
    );
  }

  if (!report) return null;

  const filters = report.filters ?? {};

  const allRows = report.rows ?? [];
  const total = allRows.length;
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const rows = allRows.slice((rowsPage - 1) * ROWS_PER_PAGE, rowsPage * ROWS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Back + header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/reports')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Reports
        </button>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#4057fe] flex items-center justify-center">
              <DocumentChartBarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Report Detail</h1>
              <p className="text-xs text-gray-400 font-mono">{report.id}</p>
            </div>
          </div>

          {/* Action bar */}
          <div className="flex items-center gap-2">
            {report.status === 'GENERATED' && (
              <button
                onClick={() => setShowDownload(true)}
                className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download
              </button>
            )}
            {(report.status === 'GENERATED' || report.status === 'FAILED') && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {regenerating
                  ? <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-[#4057fe] rounded-full animate-spin" />
                  : <ArrowPathIcon className="w-4 h-4" />
                }
                Regenerate
              </button>
            )}
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <TrashIcon className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Metadata card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">Report Info</h2>
          <MetaRow label="Status"><ReportStatusBadge status={report.status} /></MetaRow>
          <MetaRow label="Type"><ReportTypeBadge type={report.reportType} /></MetaRow>
          <MetaRow label="Scope Level">{report.scopeLevel}</MetaRow>
          <MetaRow label="Scope">{report.scopeName ?? report.scopeId ?? '—'}</MetaRow>
          <MetaRow label="Generated By">{report.generatedBy?.name ?? report.createdByName ?? '—'}</MetaRow>
          <MetaRow label="Created At">{formatDate(report.createdAt)}</MetaRow>
          <MetaRow label="Updated At">{formatDate(report.updatedAt)}</MetaRow>
        </div>

        {/* Filters card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">Applied Filters</h2>
          <MetaRow label="Report Type">{filters.reportType ?? report.reportType ?? '—'}</MetaRow>
          <MetaRow label="Scope Level">{filters.scopeLevel ?? report.scopeLevel ?? '—'}</MetaRow>
          <MetaRow label="Scope ID">{filters.scopeId ?? report.scopeId ?? '—'}</MetaRow>
          <MetaRow label="Start Date">{formatDateOnly(filters.startDate ?? report.startDate)}</MetaRow>
          <MetaRow label="End Date">{formatDateOnly(filters.endDate ?? report.endDate)}</MetaRow>
          {report.errorMessage && (
            <div className="mt-3">
              <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Error</p>
              <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3 rounded-lg whitespace-pre-wrap break-all border border-red-100 dark:border-red-800">
                {report.errorMessage}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Report rows */}
      {report.status === 'GENERATED' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              Report Data
            </h2>
            <span className="text-xs text-gray-400">
              {total.toLocaleString()} total rows
            </span>
          </div>

          <div className="overflow-x-auto">
            {rows.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-12">No data in this report.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#e9f1ff] dark:bg-gray-700/60">
                  <tr>
                    {Object.keys(rows[0]).map((col) => (
                      <th key={col} className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="px-4 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {val === '' || val === null || val === undefined ? '—' : String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Page {rowsPage} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRowsPage((p) => p - 1)}
                  disabled={rowsPage <= 1}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setRowsPage((p) => p + 1)}
                  disabled={rowsPage >= totalPages}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-white">Delete Report</h3>
              <button onClick={() => setConfirmDelete(false)}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              Are you sure you want to delete this report? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(false)} className="text-sm px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button onClick={handleDelete} className="text-sm px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showDownload && (
        <DownloadFormatModal
          reportId={id}
          onClose={() => setShowDownload(false)}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}
